/**
 * @module ProcessWorkOrderTypes
 *
 * @description
 * Comprehensive type definitions for the Process Work Order system, supporting flexible attachment formats,
 * equipment representations, and date types. These types are designed to facilitate strong type safety,
 * maintainability, and extensibility for process work order management in industrial applications.
 *
 * ## Main Types
 * - `ProcessWorkOrderUser`, `ProcessWorkOrderAssignee`: Represent users and assignees in the work order process.
 * - `ProcessWorkOrderStatus`: Enumerates possible statuses for a process work order.
 * - `ProcessWorkOrderBase`: Generic base interface for work order tasks.
 * - `ProcessWorkOrderAttachment`, `ProcessWorkOrderFormAttachmentFile`: Attachment types for backend and form usage.
 * - `ProcessWorkOrderRepetitionDto`: Discriminated union for recurring/non-recurring work orders.
 * - `ProcessWorkOrderFormValues`, `ProcessWorkOrderFormValuesEditDto`: Types for form state management.
 * - `ProcessWorkOrderCreateDto`, `ProcessWorkOrderEditDto`, `ProcessWorkOrderDto`: DTOs for API interactions, including metadata and repetition info.
 *
 * ## Usage
 * These types are intended for use in both frontend app and backend api request contexts, ensuring consistent data structures
 * for process work order creation, editing, and retrieval. They support advanced scenarios such as recurring tasks,
 * attachment management, and equipment tracking.
 */

import {TaskChecklistsOption} from '@domain/hroc-checklists'
import {
  Attachment,
  AttachmentFile,
  EquipmentData,
  EquipmentPlain,
  TaskRepetition,
  TaskRepetitionDto
} from '@hconnect/common/types'
import {type Iso8601} from '@hconnect/common/types'
import {Moment} from 'moment-timezone'

import {TaskStatus} from './task.types'

// Base types
export type ProcessWorkOrderUser = {
  id: string
  name: string
  email?: string
}

export type ProcessWorkOrderAssignee = {
  id: string
  user: ProcessWorkOrderUser
}

export const PROCESS_WORK_ORDER_STATUSES = [
  TaskStatus.Pending,
  TaskStatus.Ongoing,
  TaskStatus.Done,
  TaskStatus.Cancelled
] as const
export type ProcessWorkOrderStatus = (typeof PROCESS_WORK_ORDER_STATUSES)[number]

// Base type for process work order tasks with flexible attachment types
export interface ProcessWorkOrderBase<
  TAttachment = unknown,
  TEquipment extends EquipmentData | EquipmentPlain = EquipmentData,
  TDate extends Moment | Iso8601 = Moment
> {
  plantId: string
  title: string
  description: string
  equipment?: TEquipment
  mainEquipment?: EquipmentData
  processStage?: string
  status: ProcessWorkOrderStatus
  attachments?: TAttachment[]
  dueDate: TDate
  doNotStartBefore?: TDate
  scheduleCreatedEntitiesCount?: number
}

export interface ProcessWorkOrderChecklistFormField {
  checklists?: TaskChecklistsOption[]
}

export interface ProcessWorkOrderChecklistDto {
  checklistTemplateIds?: string[]
}

// Utility types for better type safety and maintainability
type RequiredProcessWorkOrderFields = 'plantId' | 'title' | 'description' | 'dueDate'
type OptionalProcessWorkOrderFields =
  | 'equipment'
  | 'mainEquipment'
  | 'processStage'
  | 'status'
  | 'attachments'
  | 'doNotStartBefore'

/**
 * Process work orders use string IDs on BE for attachment identification and shared attachment management.
 */
export interface ProcessWorkOrderAttachment extends Omit<Attachment, 'id'> {
  id: string
}

export interface ProcessWorkOrderFormAttachmentFile extends Omit<AttachmentFile, 'id'> {
  id?: string
}

export type ProcessWorkOrderRepetitionInfoDto = Omit<TaskRepetitionDto, 'startDate' | 'enabled'>

export type ProcessWorkOrderRepetition =
  | {
      isRecurring: true
      repetitionInfo: TaskRepetition
    }
  | {
      isRecurring: false
      repetitionInfo: never
    }

export type ProcessWorkOrderRepetitionDto =
  | {
      isRecurring: true
      repetitionInfo: ProcessWorkOrderRepetitionInfoDto
    }
  | {
      isRecurring: false
      repetitionInfo: never
    }

export type ProcessWorkOrderDueDatePreview = {
  actualCount: number
  previewDueDates?: string[]
  reachedEndDate: boolean
  reachedStoppedAfter: boolean
}

// Form-specific types
export type ProcessWorkOrderFormValues = ProcessWorkOrderBase<File, EquipmentPlain> & {
  assignees?: ProcessWorkOrderUser[]
} & ProcessWorkOrderRepetition &
  ProcessWorkOrderChecklistFormField

export type ProcessWorkOrderFormValuesEditDto = ProcessWorkOrderBase<
  ProcessWorkOrderAttachment,
  EquipmentPlain
> & {
  assignees?: ProcessWorkOrderUser[]
} & ProcessWorkOrderRepetition

// API DTO types
export type ProcessWorkOrderCreateDto = Required<
  Pick<
    ProcessWorkOrderBase<ProcessWorkOrderFormAttachmentFile>,
    RequiredProcessWorkOrderFields | 'attachments'
  >
> &
  Omit<
    ProcessWorkOrderBase<ProcessWorkOrderFormAttachmentFile, EquipmentData, Moment>,
    RequiredProcessWorkOrderFields | 'attachments'
  > &
  ProcessWorkOrderRepetitionDto & {
    assignees?: ProcessWorkOrderUser[]
  } & ProcessWorkOrderChecklistDto

export type ProcessWorkOrderEditDto = Required<
  Pick<ProcessWorkOrderBase<ProcessWorkOrderFormAttachmentFile>, RequiredProcessWorkOrderFields>
> &
  Omit<
    ProcessWorkOrderBase<ProcessWorkOrderFormAttachmentFile, EquipmentData, Moment>,
    RequiredProcessWorkOrderFields
  > &
  ProcessWorkOrderRepetitionDto & {
    id: string
    attachmentsToDelete?: string[]
  }

// Full Process work order DTO with metadata
export type ProcessWorkOrderDto = Required<
  Pick<
    ProcessWorkOrderBase<ProcessWorkOrderAttachment, EquipmentData, Iso8601>,
    RequiredProcessWorkOrderFields
  >
> &
  Pick<
    ProcessWorkOrderBase<ProcessWorkOrderAttachment, EquipmentData, Iso8601>,
    OptionalProcessWorkOrderFields
  > & {
    id: string
    assignees?: ProcessWorkOrderAssignee[]
    createdAt: Iso8601
    createdBy: string
    lastUpdatedAt: Iso8601
    lastUpdatedBy: string
    scheduleId?: string
    repetitionInfo?: ProcessWorkOrderRepetitionInfoDto
    isChecklistsCreationFailed?: boolean
  }

// Schedule types
export enum ScheduleState {
  INACTIVE = 'Inactive'
}
